import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dropdown } from '@/components/ui/select'
import { Modal } from '@/components/ui/modal'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'
import { accountStatusLabel } from '@/lib/software-format'

const OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Stopped' },
]

interface SoftwareAccountStatusModalProps {
  isOpen: boolean
  onClose: () => void
  account: { id: number; accountName?: string; email?: string; status?: string } | null
  onSuccess: () => void
}

export function SoftwareAccountStatusModal({
  isOpen,
  onClose,
  account,
  onSuccess,
}: SoftwareAccountStatusModalProps) {
  const [status, setStatus] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setStatus(account?.status && OPTIONS.some((option) => option.value === account.status) ? account.status : '')
    setReason('')
  }, [isOpen, account])

  const mutation = useMutation({
    mutationFn: async () => {
      await apiClient.put(`/software/accounts/${account?.id}/status`, {
        status,
        reason: reason.trim(),
      })
    },
    onSuccess: () => {
      toast.success(`Marked ${accountStatusLabel(status).toLowerCase()}`)
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Could not update status')
    },
  })

  const reasonOk = reason.trim().length >= 5

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update account status" size="md">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (!account || !status || !reasonOk || mutation.isPending) return
          mutation.mutate()
        }}
      >
        <p className="text-sm text-muted-foreground">
          Set <span className="font-medium text-foreground">{account?.accountName || account?.email}</span> to active, expired, or stopped.
          A reason is saved with the account.
        </p>
        <div className="space-y-2">
          <Label>Status</Label>
          <Dropdown
            value={status}
            onChange={setStatus}
            placeholder="Select a status"
            options={OPTIONS}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="account-status-reason">Reason</Label>
          <Textarea
            id="account-status-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this account being stopped, expired, or turned back on?"
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">At least 5 characters. People on this account can see this reason.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!status || !reasonOk || mutation.isPending}>
            {mutation.isPending && <LoadingSpinner size="sm" className="mr-2" />}
            Save status
          </Button>
        </div>
      </form>
    </Modal>
  )
}
